/**
 * The first-party window Apple Pay (and Google Pay, where the frame cannot show it) runs in when
 * the give box is framed (GH-847). Moved out of `EmbedGive.tsx` unchanged in GH-877, to keep that
 * file under the 800-line ceiling.
 */
import { embedWalletPath } from "@givehere/contracts";
import { useEffect, useRef, useState } from "react";
import { fromOwnWindow, type WalletGiftMessage, type WalletResultMessage } from "./embed-frame.js";

export type WalletGift = {
  readonly amountMinor: string;
  readonly costSupport: boolean;
  readonly designationId: string | null;
  readonly eventId: string | null;
};

/**
 * The first-party window Apple Pay runs in when the box is framed, and its one-question channel:
 * the window asks for the gift, the box answers with its context token and the donor's choices
 * (same origin only), and the window reports back the donation it opened. The address carries the
 * same choices as hints, so a window opened without its opener can still stand on its own.
 */
export function useWalletWindow({
  organizationId,
  token,
  widgetId,
  gift,
  onResult,
}: {
  organizationId: string;
  token: string;
  /** The saved widget (GH-851), so a window stood up on its own mints under the same rules. */
  widgetId: string | null;
  gift: WalletGift | null;
  onResult: (result: WalletResultMessage) => void;
}): { open: () => void; blockedUrl: string | null } {
  const popup = useRef<Window | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const latest = useRef({ token, gift, onResult });
  latest.current = { token, gift, onResult };

  useEffect(() => {
    const listen = (event: MessageEvent) => {
      // A window opened through the "blocked" link has no handle here yet: adopt it only if it is
      // on this origin and this window is its opener — which only a window we opened can say.
      const kind = (event.data as { type?: unknown } | null)?.type;
      const fromWallet = kind === "givehere:wallet-ready" || kind === "givehere:wallet-result";
      // Adopt only a window speaking the wallet's own messages (GH-877: never the sign-in window).
      if (!popup.current && fromWallet && event.origin === window.location.origin) {
        const source = event.source as Window | null;
        try {
          if (source && source !== window && source.opener === window) popup.current = source;
        } catch {
          // Not ours to inspect.
        }
      }
      if (!fromOwnWindow(event, popup.current)) return;
      const data = event.data as { type?: unknown } | null;
      if (!data || typeof data !== "object") return;
      const current = latest.current;
      if (data.type === "givehere:wallet-ready" && current.gift) {
        const answer: WalletGiftMessage = {
          type: "givehere:wallet-gift",
          contextToken: current.token,
          amountMinor: current.gift.amountMinor,
          costSupport: current.gift.costSupport,
          designationId: current.gift.designationId,
        };
        popup.current?.postMessage(answer, window.location.origin);
        return;
      }
      if (data.type === "givehere:wallet-result") {
        const result = data as WalletResultMessage;
        if (typeof result.donationId === "string" && typeof result.accessToken === "string") {
          current.onResult(result);
        }
      }
    };
    window.addEventListener("message", listen);
    return () => window.removeEventListener("message", listen);
  }, []);

  const open = () => {
    const current = latest.current.gift;
    if (!current) return;
    const url = new URL(embedWalletPath(organizationId), window.location.origin);
    url.searchParams.set("amount", current.amountMinor);
    url.searchParams.set("cover", current.costSupport ? "1" : "0");
    if (widgetId) url.searchParams.set("w", widgetId);
    if (current.designationId) url.searchParams.set("designation", current.designationId);
    if (current.eventId) url.searchParams.set("event", current.eventId);
    // `_blank`, not a named window: a name could match a window some other page already opened.
    const opened = window.open(url.toString(), "_blank", "popup,width=440,height=680");
    if (!opened) {
      setBlockedUrl(url.toString());
      return;
    }
    setBlockedUrl(null);
    popup.current = opened;
  };

  return { open, blockedUrl };
}
